import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, MapPin } from 'lucide-react';
import type { PostalCodeToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { usePostalCodesList, usePostalCodeCountries, usePostalCodeRegions, usePostalCodeMutations } from '~/hooks/use-postal-codes';
import { tokenToForm, type PostalCodeFormValues } from '~/lib/schemas/postal-code';
import { PostalCodeEditDialog } from './postal-code-edit-dialog';

/** Postal codes CRUD surface — Code/Country/Region/Area/City/Visible/Sort. */
export function PostalCodesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = usePostalCodesList();
	const { data: countries = [] } = usePostalCodeCountries();
	const { data: regions = [] } = usePostalCodeRegions();
	const { remove } = usePostalCodeMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<PostalCodeFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<PostalCodeToken | null>(null);

	const countryMap = useMemo(() => new Map(countries.map(c => [c.id, c.nm])), [countries]);
	const regionMap = useMemo(() => new Map(regions.map(r => [r.id, r.nm])), [regions]);
	const countryOf = (p: PostalCodeToken) => (p.cid == null ? '' : (countryMap.get(p.cid) ?? ''));
	const regionOf = (p: PostalCodeToken) => (p.rid == null ? '' : (regionMap.get(p.rid) ?? ''));

	const columns = useMemo<SystemGridColumn<PostalCodeToken>[]>(
		() => [
			{
				key: 'code',
				header: t('system.postalCodes.columns.code'),
				width: '120px',
				sortValue: p => p.cd,
				exportValue: p => p.cd,
				render: p => <span className="truncate font-mono text-xs font-medium text-foreground">{p.cd}</span>
			},
			{
				key: 'area',
				header: t('system.postalCodes.columns.area'),
				width: 'minmax(0,1fr)',
				sortValue: p => p.ar ?? '',
				exportValue: p => p.ar ?? '',
				render: p => <span className="truncate text-sm font-medium text-foreground">{p.ar || '—'}</span>
			},
			{
				key: 'city',
				header: t('system.postalCodes.columns.city'),
				width: '160px',
				sortValue: p => p.ct ?? '',
				exportValue: p => p.ct ?? '',
				render: p => <span className="truncate text-sm text-foreground-muted">{p.ct || '—'}</span>
			},
			{
				key: 'district',
				header: t('system.postalCodes.columns.district'),
				width: '160px',
				sortValue: p => p.dst ?? '',
				exportValue: p => p.dst ?? '',
				render: p => <span className="truncate text-sm text-foreground-muted">{p.dst || '—'}</span>
			},
			{
				key: 'region',
				header: t('system.postalCodes.columns.region'),
				width: '150px',
				sortValue: p => regionOf(p),
				exportValue: p => regionOf(p),
				render: p => <span className="truncate text-sm text-foreground-muted">{regionOf(p) || '—'}</span>
			},
			{
				key: 'country',
				header: t('system.postalCodes.columns.country'),
				width: '140px',
				sortValue: p => countryOf(p),
				exportValue: p => countryOf(p),
				render: p => <span className="truncate text-sm text-foreground-muted">{countryOf(p) || '—'}</span>
			},
			{
				key: 'visible',
				header: t('system.postalCodes.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: p => (p.iv ? 1 : 0),
				exportValue: p => (p.iv ? t('common.yes') : t('common.no')),
				render: p => (p.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.postalCodes.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: p => p.si ?? 0,
				exportValue: p => p.si ?? 0,
				render: p => <span className="text-sm tabular-nums text-foreground-muted">{p.si}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, countryMap, regionMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<PostalCodeToken>
				gridId="postal-codes"
				title={t('system.postalCodes.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={p => p.id ?? p.cd}
				columns={columns}
				searchPredicate={(p, q) => `${p.cd ?? ''} ${p.ar ?? ''} ${p.ct ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'code', dir: 'asc' }}
				emptyIcon={<MapPin size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.postalCodes.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={p => {
					setEditing(tokenToForm(p));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="postal-codes"
			/>

			<PostalCodeEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} countries={countries} regions={regions} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.postalCodes.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.postalCodes.deleteBody', { name: deleteRow?.cd ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
